import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import removeRedEye from "../../../assets/icons/removeRedEye.svg";
import { TableSkeleton } from "src/utils/TableSkeleton";
import FoodLoader from "src/components/Loader";
import { format } from "date-fns";
import CustomSelect from "../../../components/ui/CustomSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { saveAs } from "file-saver";

interface AgentTableProps {
  data: any[];
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  onPageChange: React.Dispatch<React.SetStateAction<number>>;
  itemsPerPage: number;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  setSearching: (searching: boolean) => void;
  filter: string;
  setFilter: (filter: string) => void;
  loading: boolean;
}

const AgentTable: React.FC<AgentTableProps> = ({
  data,
  itemsPerPage,
  currentPage,
  setCurrentPage,
  totalPages,
  filter,
  setFilter,
  loading,
  searchTerm,
  setSearchTerm,
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage;

  const downloadCSV = () => {
    if (data.length === 0) return;

    const csvData = data.map((agent) => ({
      "S/N": data.indexOf(agent) + 1,
      "First Name": agent.first_name || "",
      "Last Name": agent.last_name || "",
      Email: agent.email || "",
      Phone: agent.phone || "",
      Status: agent.is_suspended ? "Suspended" : "Active",
      "Checked by Admin": agent.is_checked_by_admin
        ? "Activated"
        : "Not Active",
      "Agent Type": agent.agent_type || "",
      "Account Status": agent.account_status || "",
      Rating: agent.rating || 0,
      "Total Deliveries": agent.total_deliveries || 0,
      "Total Delivery Distance": agent.total_delivery_distance || 0,
      "Average Delivery Time": agent.average_delivery_time || 0,
      "Total Suspensions": agent.total_suspensions || 0,
      "Registration Date": agent.created_at
        ? format(new Date(agent.created_at), "PPpp")
        : "N/A",
      "Last Login": agent.last_login
        ? format(new Date(agent.last_login), "PPpp")
        : "N/A",
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(","),
      ...csvData.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "agents_data.csv");
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
        <FoodLoader
          size="md"
          text="Fetching Agents"
          theme="amber"
          className="scale-75"
        />
      </div>
    );
  }

  return (
    <div className="bg-amber-50 rounded-xl shadow-sm">
      {/* Filter & Download Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-6 border-b border-amber-200 gap-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-amber-900">
            Filter by:
          </label>
          <div className="w-32">
            <CustomSelect
              value={filter}
              onChange={(value) => setFilter(value as string)}
              options={[
                { label: "All", value: "all" },
                { label: "Today", value: "day" },
                { label: "This Week", value: "week" },
                { label: "This Month", value: "month" },
                { label: "This Year", value: "year" },
              ]}
              placeholder="Filter"
            />
          </div>
          {filter !== "all" && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                Filtered by {filter}
              </span>
              <button
                onClick={() => setFilter("all")}
                className="text-xs text-amber-600 hover:text-amber-800 underline"
              >
                Clear filter
              </button>
            </div>
          )}
        </div>

        {/* Download Button */}
        <button
          onClick={downloadCSV}
          disabled={data.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-md border border-amber-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faDownload} className="w-4 h-4" />
          Download CSV
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-amber-100/70 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3 border-b border-amber-200 w-[60px]">
                S/N
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                First Name
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Last Name
              </th>
              <th className="px-5 py-3 border-b border-amber-200">Email</th>
              <th className="px-5 py-3 border-b border-amber-200">Status</th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Checked by Admin
              </th>
              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                Registration Date
              </th>
              <th className="px-5 py-3 border-b border-amber-200 w-[80px]">
                View
              </th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((agent, index) => (
                <tr key={agent._id} className="hover:bg-amber-50/80">
                  <td className="px-5 py-3 border-b border-amber-100 font-medium">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    {agent.first_name}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    {agent.last_name}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100 max-w-[200px] truncate">
                    {agent.email}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        agent.is_suspended
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {agent.is_suspended ? "Suspended" : "Active"}
                    </span>
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    {agent.is_checked_by_admin ? (
                      <span className="text-green-600 font-medium">
                        Activated
                      </span>
                    ) : (
                      <span className="text-red-600 font-medium">
                        Not Active
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100 max-w-[200px] truncate">
                    {agent.created_at
                      ? format(new Date(agent.created_at), "PPpp")
                      : "N/A"}
                  </td>
                  <td className="px-5 py-3 border-b border-amber-100">
                    <Link to={`/pr-admin/agents/agent-details/${agent._id}`}>
                      <button
                        className="p-1 rounded-full hover:bg-amber-100 transition-colors"
                        aria-label="View agent details"
                      >
                        <img
                          src={removeRedEye || "/placeholder.svg"}
                          alt="View"
                          className="h-5 w-5"
                        />
                      </button>
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-10 text-gray-500">
                  {searchTerm
                    ? "No agents found matching your search."
                    : filter !== "all"
                    ? `No agents found for the selected filter (${filter}).`
                    : "No agents found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="py-4 px-6 border-t border-amber-200">
        <div className="flex items-center justify-between">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-md border border-amber-200 ${
              currentPage === 1
                ? "bg-amber-50 text-gray-400 cursor-not-allowed"
                : "bg-amber-50 text-amber-900 hover:bg-amber-100"
            }`}
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={data.length < itemsPerPage}
            className={`px-4 py-2 rounded-md border border-amber-200 ${
              data.length < itemsPerPage
                ? "bg-amber-50 text-gray-400 cursor-not-allowed"
                : "bg-amber-50 text-amber-900 hover:bg-amber-100"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentTable;
